import React from 'react';
import { notifications, extractApiErrorMessage } from '../../utils';
import { Title, Loader, ClearFilters, StatusChip } from '../../elements';
import type { StatusChipColor } from '../../elements';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Button,
  Collapse,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  Tooltip
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { HealthSystemAPIControllersDeviceResponsesHealthInjectorActivityResponse } from '../../api/HealthSystemApi';

interface HealthInjectorActivitiesTableProps {
  injectorId: string;
}

const activityTypes = [
  'DeviceRegistered',
  'FarmAttached',
  'FarmDetached',
  'FarmChanged',
  'MobileConnection',
  'FirmwareUpdated',
  'ManualUpdate',
  'DeviceDisabled',
  'DeviceEnabled',
  'ConfigurationChanged'
];

const HealthInjectorActivitiesTable: React.FC<HealthInjectorActivitiesTableProps> = ({ injectorId }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [activityType, setActivityType] = React.useState<string>('');
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(null);
  const [toDate, setToDate] = React.useState<Dayjs | null>(null);
  const [activities, setActivities] = React.useState<
    HealthSystemAPIControllersDeviceResponsesHealthInjectorActivityResponse[]
  >([]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  // expanded rows for firmware details
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRowExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setActivities([]);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const loadActivities = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.device
      .deviceGetHealthInjectorActivities({
        id: injectorId,
        page: searchPage,
        size: rowsPerPage,
        activityType: activityType || undefined,
        fromDate: fromDate ? fromDate.toISOString() : undefined,
        toDate: toDate ? toDate.toISOString() : undefined
      })
      .then((result) => {
        setActivities(result.data.activities);
        setTotal(result.data.totalCount);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadActivities();
      loadData();
    }
  }, [loaded]);

  const clearFilters = () => {
    setActivityType('');
    setFromDate(null);
    setToDate(null);
    setPage(0);
  };

  const getActivityTypeChipColor = (type: string): StatusChipColor => {
    switch (type) {
      case 'DeviceRegistered':
        return 'emerald';
      case 'FarmAttached':
        return 'blue';
      case 'FarmDetached':
        return 'orange';
      case 'FarmChanged':
        return 'purple';
      case 'MobileConnection':
        return 'cyan';
      case 'FirmwareUpdated':
        return 'indigo';
      case 'ManualUpdate':
        return 'teal';
      case 'DeviceDisabled':
        return 'red';
      case 'DeviceEnabled':
        return 'green';
      case 'ConfigurationChanged':
        return 'amber';
      default:
        return 'gray';
    }
  };

  const renderFarmName = (name: string | null | undefined, isSource: boolean) => {
    if (name) {
      return (
        <Tooltip title={isSource ? t('f-com-previous-farm') : t('f-com-new-farm')}>
          <span className="flex items-center gap-1">
            <BusinessIcon fontSize="small" className="text-blue-600" />
            <span className="font-medium">{name}</span>
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip title={isSource ? t('f-com-no-previous-farm') : t('f-com-no-farm-assigned')}>
        <span className="flex items-center gap-1 text-gray-400 italic">
          <RemoveCircleOutlineIcon fontSize="small" />
          <span>{t('f-com-none')}</span>
        </span>
      </Tooltip>
    );
  };

  const renderFarmTransition = (previousFarm: string | null | undefined, newFarm: string | null | undefined) => {
    return (
      <div className="flex items-center gap-2">
        {renderFarmName(previousFarm, true)}
        <ArrowForwardIcon fontSize="small" className="text-gray-500" />
        {renderFarmName(newFarm, false)}
      </div>
    );
  };

  const tryParseJson = (value: string | undefined | null): object | null => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const hasFirmwareJsonData = (
    metadata: { previousFirmwareVersion?: string | null; firmwareVersion?: string | null } | undefined
  ) => {
    if (!metadata) return false;
    const prev = tryParseJson(metadata.previousFirmwareVersion);
    const curr = tryParseJson(metadata.firmwareVersion);
    return prev !== null || curr !== null;
  };

  const renderFirmwareExpandedContent = (metadata: {
    previousFirmwareVersion?: string | null;
    firmwareVersion?: string | null;
  }) => {
    const prevJson = tryParseJson(metadata.previousFirmwareVersion);
    const currJson = tryParseJson(metadata.firmwareVersion);

    return (
      <div className="flex gap-4 p-4">
        {prevJson && (
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-600 mb-2">{t('f-com-previous')}:</div>
            <pre className="text-xs bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-64 font-mono">
              {JSON.stringify(prevJson, null, 2)}
            </pre>
          </div>
        )}
        {currJson && (
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-600 mb-2">{t('f-com-current')}:</div>
            <pre className="text-xs bg-green-50 border border-green-200 rounded p-3 overflow-auto max-h-64 font-mono">
              {JSON.stringify(currJson, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  const renderActivityDetails = (
    activity: HealthSystemAPIControllersDeviceResponsesHealthInjectorActivityResponse,
    isExpanded: boolean,
    onToggle: () => void
  ) => {
    const { activityType, farmName, previousFarmName, metadata } = activity;

    // Farm change activities - show farm transition with icons
    if (activityType === 'FarmChanged') {
      return renderFarmTransition(previousFarmName, farmName);
    }
    if (activityType === 'FarmAttached') {
      return renderFarmTransition(null, farmName);
    }
    if (activityType === 'FarmDetached') {
      return renderFarmTransition(previousFarmName, null);
    }

    // Firmware updated - show expand button if JSON data exists
    if (activityType === 'FirmwareUpdated' && metadata) {
      const prev = metadata.previousFirmwareVersion;
      const curr = metadata.firmwareVersion;

      if (hasFirmwareJsonData(metadata)) {
        return (
          <div className="flex items-center gap-2">
            <IconButton size="small" onClick={onToggle} sx={V4_ICON_BUTTON_SMALL}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <span className="text-sm text-gray-600">
              {isExpanded ? t('f-com-hide-details') : t('f-com-show-details')}
            </span>
          </div>
        );
      }

      // Simple text transition for non-JSON data
      if (prev && curr) return `${prev} → ${curr}`;
      if (curr) return `→ ${curr}`;
    }

    // For other activities, show relevant metadata if available
    if (metadata?.deviceInfo) {
      return metadata.deviceInfo;
    }

    return '-';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Icon color="primary" fontSize="large">
          history
        </Icon>
        <Title>{t('f-logs-activities')}</Title>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item md={2} xs={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="activity-type-label">{t('f-com-activity-type')}</InputLabel>
                <Select
                  labelId="activity-type-label"
                  id="activity-type-select"
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value as string)}
                  label={t('f-com-activity-type')}
                >
                  <MenuItem value="">
                    <em>{t('f-com-all')}</em>
                  </MenuItem>
                  {activityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item md={2} xs={6}>
              <DatePicker
                label={t('f-com-from-date')}
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue as Dayjs | null)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>

            <Grid item md={2} xs={6}>
              <DatePicker
                label={t('f-com-to-date')}
                value={toDate}
                onChange={(newValue) => setToDate(newValue as Dayjs | null)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>

            <Grid item>
              <ClearFilters onClick={clearFilters} />
            </Grid>

            <Grid item>
              <Button
                id="searchBtn"
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setLoaded(false)}
                startIcon={<Icon>search</Icon>}
              >
                {t('f-search')}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </div>

      {/* Loader */}
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {/* Table Card */}
      {loaded && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-com-date-time')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-com-activity-type')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 min-w-72">{t('f-com-details')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-com-performed-by')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('m-global-comments')}</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {activities.map((activity) => {
                  const isExpanded = expandedRows.has(activity.id);
                  const showExpandedRow =
                    activity.activityType === 'FirmwareUpdated' &&
                    activity.metadata &&
                    hasFirmwareJsonData(activity.metadata);

                  return (
                    <React.Fragment key={activity.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-900">
                          {new Date(activity.activityTimestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <StatusChip
                            label={activity.activityType}
                            color={getActivityTypeChipColor(activity.activityType)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {renderActivityDetails(activity, isExpanded, () => toggleRowExpanded(activity.id))}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{activity.performedByUserName || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{activity.notes || '-'}</td>
                      </tr>
                      {showExpandedRow && (
                        <tr>
                          <td colSpan={5} className="p-0">
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <div className="bg-gray-50 border-t border-gray-200">
                                {renderFirmwareExpandedContent(activity.metadata!)}
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {activities.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      {t('f-no-data')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="border-t border-gray-200">
            <TablePagination
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              component="div"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthInjectorActivitiesTable;
